import { useEffect, useState } from "react";
import type { BookListItem, DashboardResponse, MediaListItem, PlaceListItem } from "@atlas/contracts";
import { ApiError, fetchBooks, fetchDashboard, fetchMedia, fetchPlaces } from "../lib/api";
import { formatNumberFa, formatRelativeTime, formatToman } from "../lib/format";
import { Icon, type IconName } from "./Icon";
import { LoadingState, ErrorState } from "./StateViews";

interface DashboardViewProps {
  onNavigate: (path: string) => void;
  onOpenSearch: () => void;
}

interface ContinueData {
  book: BookListItem | null;
  movie: MediaListItem | null;
  place: PlaceListItem | null;
}

type ContinueEntry =
  | { kind: "movie"; data: MediaListItem }
  | { kind: "book"; data: BookListItem }
  | { kind: "place"; data: PlaceListItem };

function buildContinueEntries(continueData: ContinueData): { featured: ContinueEntry | null; secondary: ContinueEntry[] } {
  const pool: Partial<Record<ContinueEntry["kind"], ContinueEntry>> = {};
  if (continueData.movie) pool.movie = { kind: "movie", data: continueData.movie };
  if (continueData.book) pool.book = { kind: "book", data: continueData.book };
  if (continueData.place) pool.place = { kind: "place", data: continueData.place };

  const featuredKind = (["movie", "book", "place"] as const).find((k) => pool[k]);
  const featured = featuredKind ? pool[featuredKind]! : null;
  const secondary = (["place", "book", "movie"] as const)
    .filter((k) => k !== featuredKind && pool[k])
    .map((k) => pool[k]!);

  return { featured, secondary };
}

function countFor(
  dashboard: DashboardResponse,
  section: "books" | "media" | "places" | "projects",
  status: string,
): number {
  return dashboard[section].byStatus.find((entry) => entry.status === status)?.count ?? 0;
}

export function DashboardView({ onNavigate, onOpenSearch }: DashboardViewProps) {
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [continueData, setContinueData] = useState<ContinueData>({ book: null, movie: null, place: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activityOpen, setActivityOpen] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    Promise.all([
      fetchDashboard(controller.signal),
      fetchBooks({ status: "reading", limit: 1 }, controller.signal).catch(() => null),
      fetchMedia({ status: "watch", limit: 1 }, controller.signal).catch(() => null),
      fetchPlaces({ status: "want", limit: 1 }, controller.signal).catch(() => null),
    ])
      .then(([dashboardRes, booksRes, mediaRes, placesRes]) => {
        setDashboard(dashboardRes);
        setContinueData({
          book: booksRes?.items[0] ?? null,
          movie: mediaRes?.items[0] ?? null,
          place: placesRes?.items[0] ?? null,
        });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setDashboard(null);
        setError(err instanceof ApiError ? err.message : "بارگذاری خانه با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [reloadToken]);

  const { featured: featuredContinue, secondary: secondaryContinue } = buildContinueEntries(continueData);

  return (
    <div>
      <header className="page-header" style={{ marginBottom: 24 }}>
        <span className="shell__brand">اطلس</span>
        <span className="shell__header-actions">
          <button type="button" className="icon-btn" onClick={onOpenSearch} aria-label="جست‌وجوی سراسری">
            <Icon name="search" width={16} height={16} aria-hidden="true" />
          </button>
          <span className="avatar-btn" aria-hidden="true">
            ا
          </span>
        </span>
      </header>

      {loading && !dashboard && <LoadingState label="در حال بارگذاری خانه…" />}
      {error && <ErrorState message={error} onRetry={() => setReloadToken((t) => t + 1)} />}

      {!error && dashboard && (
        <>
          <div className="home-greeting">
            <p className="home-greeting__hello">سلام امیر</p>
            <p className="home-greeting__question">امروز از کجا ادامه بدیم؟</p>
          </div>

          <div className="continue-heading">
            <span className="continue-heading__accent" aria-hidden="true" />
            <span className="home-section-label" style={{ marginBottom: 0 }}>
              ادامه بده
            </span>
          </div>
          {!featuredContinue && (
            <p className="filter-summary" style={{ marginBottom: 22 }}>
              چیزی در حال پیگیری نیست.
            </p>
          )}
          {featuredContinue && (
            <FeaturedContinueCard entry={featuredContinue} onNavigate={onNavigate} />
          )}
          {secondaryContinue.length > 0 && (
            <div className="continue-secondary">
              {secondaryContinue.map((entry) => (
                <CompactContinueCard key={entry.data.id} entry={entry} onNavigate={onNavigate} />
              ))}
            </div>
          )}

      {dashboard.recentActivity.length > 0 && (
        <>
          <button type="button" className="activity-toggle" onClick={() => setActivityOpen((v) => !v)}>
            <span className="home-section-label" style={{ marginBottom: 0 }}>
              آخرین فعالیت‌ها
            </span>
            <span className="home-section-hint" style={{ margin: 0 }}>
              {activityOpen ? "بستن" : "نمایش"}
            </span>
          </button>
          {activityOpen && (
            <div className="activity-list">
              {dashboard.recentActivity.map((item) => (
                <div key={item.id} className="activity-item">
                  <span className="activity-item__date">{formatRelativeTime(item.createdAt)}</span> — {item.summary}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <p className="home-section-label">نگاه کلی</p>
      <div className="overview-list">
        <OverviewRow
          icon="book"
          label="کتاب‌ها"
          sub={`${formatNumberFa(countFor(dashboard, "books", "reading"))} در حال خواندن`}
          count={dashboard.books.available ? formatNumberFa(dashboard.books.total) : "—"}
          onClick={() => onNavigate("/books")}
        />
        <OverviewRow
          icon="film"
          label="فیلم و سریال"
          sub={`${formatNumberFa(countFor(dashboard, "media", "watch"))} در حال تماشا`}
          count={dashboard.media.available ? formatNumberFa(dashboard.media.total) : "—"}
          onClick={() => onNavigate("/media")}
        />
        <OverviewRow
          icon="map"
          label="مکان‌ها"
          sub={`${formatNumberFa(countFor(dashboard, "places", "want"))} می‌خواهم بروم`}
          count={dashboard.places.available ? formatNumberFa(dashboard.places.total) : "—"}
          onClick={() => onNavigate("/places")}
        />
        <OverviewRow
          icon="flag"
          label="پروژه‌ها"
          sub={dashboard.projects.available ? `${formatNumberFa(countFor(dashboard, "projects", "active"))} فعال` : "در دسترس نیست"}
          count={dashboard.projects.available ? formatNumberFa(dashboard.projects.total) : "—"}
          onClick={() => onNavigate("/more/projects")}
        />
        <OverviewRow
          icon="wallet"
          label="مالی"
          sub={dashboard.finance.available ? `این ماه: ${formatToman(dashboard.finance.netToman)}` : "در دسترس نیست"}
          count={dashboard.finance.available ? formatToman(dashboard.finance.netToman) : "—"}
          onClick={() => onNavigate("/more/finance")}
        />
      </div>
        </>
      )}
    </div>
  );
}

interface ContinuePresentation {
  path: string;
  pillLabel: string;
  pillNeutral: boolean;
  title: string;
  sub: string;
  assetUrl?: string;
  alt: string;
  placeholder: string;
  progressPct: number | null;
}

function presentContinueEntry(entry: ContinueEntry): ContinuePresentation {
  if (entry.kind === "movie") {
    const { data } = entry;
    const sub =
      data.type === "series" && typeof data.season === "number" && typeof data.episode === "number"
        ? `فصل ${formatNumberFa(data.season)} · قسمت ${formatNumberFa(data.episode)}`
        : data.year
          ? `سال ${data.year}`
          : "—";
    return {
      path: `/media/${data.slug}`,
      pillLabel: "در حال تماشا",
      pillNeutral: false,
      title: data.titleFa,
      sub,
      assetUrl: data.assetUrl,
      alt: data.titleFa,
      placeholder: "پوستر",
      progressPct: null,
    };
  }

  if (entry.kind === "book") {
    const { data } = entry;
    const hasProgress = typeof data.totalPages === "number" && data.totalPages > 0;
    return {
      path: `/books/${data.slug}`,
      pillLabel: "در حال خواندن",
      pillNeutral: false,
      title: data.titleFa,
      sub: hasProgress
        ? `صفحه ${formatNumberFa(data.currentPage ?? 0)} از ${formatNumberFa(data.totalPages!)}`
        : "بدون گزارش پیشرفت",
      assetUrl: data.assetUrl,
      alt: data.titleFa,
      placeholder: "جلد کتاب",
      progressPct: hasProgress ? Math.min(100, Math.round(((data.currentPage ?? 0) / data.totalPages!) * 100)) : null,
    };
  }

  const { data } = entry;
  return {
    path: `/places/${data.slug}`,
    pillLabel: "می‌خواهم بروم",
    pillNeutral: true,
    title: data.title,
    sub: data.area || "—",
    assetUrl: data.assetUrl,
    alt: data.title,
    placeholder: "عکس مکان",
    progressPct: null,
  };
}

function FeaturedContinueCard({ entry, onNavigate }: { entry: ContinueEntry; onNavigate: (path: string) => void }) {
  const p = presentContinueEntry(entry);
  return (
    <button type="button" className="continue-featured" onClick={() => onNavigate(p.path)}>
      <div className="continue-featured__body">
        <span className={`continue-card__pill${p.pillNeutral ? " continue-card__pill--neutral" : ""}`}>
          {p.pillLabel}
        </span>
        <p className="continue-featured__title">{p.title}</p>
        <p className="continue-featured__sub">{p.sub}</p>
        {p.progressPct !== null && (
          <div className="progress-track continue-featured__progress">
            <div className="progress-track__fill" style={{ width: `${p.progressPct}%` }} />
          </div>
        )}
      </div>
      <div className="continue-featured__media">
        {p.assetUrl ? (
          <img src={p.assetUrl} alt={p.alt} className="continue-featured__image" />
        ) : (
          <span>{p.placeholder}</span>
        )}
      </div>
    </button>
  );
}

function CompactContinueCard({ entry, onNavigate }: { entry: ContinueEntry; onNavigate: (path: string) => void }) {
  const p = presentContinueEntry(entry);
  return (
    <button type="button" className="continue-card continue-card--compact" onClick={() => onNavigate(p.path)}>
      <div className="continue-card__media">
        {p.assetUrl ? (
          <img src={p.assetUrl} alt={p.alt} className="continue-card__image" />
        ) : (
          <span>{p.placeholder}</span>
        )}
        <span className={`continue-card__pill${p.pillNeutral ? " continue-card__pill--neutral" : ""}`}>
          {p.pillLabel}
        </span>
      </div>
      <p className="continue-card__title">{p.title}</p>
      <p className="continue-card__sub">{p.sub}</p>
    </button>
  );
}

function OverviewRow({
  icon,
  label,
  sub,
  count,
  onClick,
}: {
  icon: IconName;
  label: string;
  sub: string;
  count: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="overview-row" onClick={onClick}>
      <span className="overview-row__icon">
        <Icon name={icon} width={16} height={16} aria-hidden="true" />
      </span>
      <span className="overview-row__body">
        <span className="overview-row__label">{label}</span>
        <span className="overview-row__sub">{sub}</span>
      </span>
      <span className="overview-row__count">{count}</span>
    </button>
  );
}
